import { createFileRoute, Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import {
  ArrowRight,
  Search,
  AlertTriangle,
  Clock,
  TrendingDown,
  Zap,
  Globe,
  BarChart,
  ShieldCheck,
} from 'lucide-react'

export const Route = createFileRoute('/_site/blog/downtime-seo-impact')({
  component: DowntimeSeoImpactPage,
  head: () => ({
    meta: [
      {
        title:
          'How Website Downtime Affects Your SEO Rankings — Uptrack',
      },
      {
        name: 'description',
        content:
          'Downtime causes Google crawl errors, deindexing risk and Core Web Vitals failures. How faster outage detection protects your search rankings.',
      },
      {
        property: 'og:title',
        content: 'How Website Downtime Affects Your SEO Rankings — Uptrack',
      },
      {
        property: 'og:description',
        content:
          'Google crawl errors during downtime can push your pages out of search results. Here\'s how downtime hurts SEO and how to minimize the damage.',
      },
      {
        property: 'og:image',
        content: 'https://uptrack.app/og/blog-downtime-seo-impact.png',
      },
      { property: 'og:type', content: 'article' },
      {
        property: 'og:url',
        content: 'https://uptrack.app/blog/downtime-seo-impact',
      },
      { property: 'article:published_time', content: '2026-04-01' },
    ],
    links: [
      {
        rel: 'canonical',
        href: 'https://uptrack.app/blog/downtime-seo-impact',
      },
    ],
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: 'How Website Downtime Affects Your SEO Rankings',
          description:
            'Google crawl errors during downtime can push your pages out of search results. Here\'s how downtime hurts SEO and how to minimize the damage.',
          datePublished: '2026-04-01',
          dateModified: '2026-05-22',
          author: { '@type': 'Organization', name: 'Uptrack' },
          publisher: {
            '@type': 'Organization',
            name: 'Uptrack',
            url: 'https://uptrack.app',
          },
          mainEntityOfPage:
            'https://uptrack.app/blog/downtime-seo-impact',
        }),
      },
    ],
  }),
})

function DowntimeSeoImpactPage() {
  return (
    <>

      <>
        <article className="article-prose container mx-auto max-w-2xl px-4 py-16">
          <p className="mb-3 text-sm text-muted-foreground">April 1, 2026</p>
          <h1 className="font-sans mb-6 text-4xl font-semibold">
            How website downtime affects your SEO rankings
          </h1>

          {/* Intro */}
          <section className="mb-12">
            <p className="mb-4 text-lg leading-[1.7] text-muted-foreground">
              You spend months building backlinks, optimizing content, and
              climbing from page 3 to page 1. Then your server goes down for
              4 hours on a Tuesday afternoon. Googlebot shows up, gets a 500
              error, and records a crawl failure. If it happens again next
              week, Google starts questioning whether your page should be
              in the index at all.
            </p>
            <p className="text-lg leading-[1.7] text-muted-foreground">
              SEO damage from downtime is invisible until it isn't. You won't
              see a notification saying "your rankings dropped because of
              downtime." You'll just notice organic traffic slowly declining
              in Google Analytics, with no obvious cause. By the time you
              connect the dots, the damage is done.
            </p>
          </section>

          {/* How Google handles downtime */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">
              What happens when Googlebot hits your downtime
            </h2>
            <p className="mb-6 text-lg leading-[1.7] text-muted-foreground">
              Google's crawler visits your site on its own schedule. You
              don't control when it arrives. When Googlebot encounters an
              error, it follows a specific process that can escalate from
              "minor inconvenience" to "deindexed" faster than you'd expect.
            </p>

            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-status-degraded/20 text-sm font-semibold text-status-degraded">
                  1
                </div>
                <div>
                  <h3 className="font-sans mb-1 font-semibold">First crawl error: grace period</h3>
                  <p className="text-lg leading-[1.7] text-body">
                    Googlebot encounters a 500 or timeout. It notes the error
                    but keeps your page in the index with its current ranking.
                    Google is patient with isolated errors. The page is
                    scheduled for a re-crawl soon.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-status-degraded-soft text-sm font-semibold text-status-degraded">
                  2
                </div>
                <div>
                  <h3 className="font-sans mb-1 font-semibold">Repeated errors: crawl rate reduction</h3>
                  <p className="text-lg leading-[1.7] text-body">
                    If subsequent crawl attempts also fail, Google reduces
                    how often it crawls your site. This is meant to be polite
                    (don't hammer a struggling server), but it means your
                    new content takes longer to get indexed. Your competitors'
                    fresh content gets crawled and ranked while yours waits.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-status-down/20 text-sm font-semibold text-status-down">
                  3
                </div>
                <div>
                  <h3 className="font-sans mb-1 font-semibold">Persistent errors: deindexing</h3>
                  <p className="text-lg leading-[1.7] text-body">
                    If your page consistently returns errors over days or
                    weeks, Google may remove it from the index entirely. The
                    page disappears from search results. Getting re-indexed
                    requires the page to be stable again and for Googlebot to
                    successfully crawl it multiple times. This can take weeks.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6 rounded-lg border border-status-degraded/30 bg-status-degraded/5 p-4">
              <p className="text-lg leading-[1.7] text-body">
                <span className="font-semibold text-status-degraded">
                  Google's own words:
                </span>{' '}
                "If Googlebot receives an HTTP 500 status code from your
                server, it will reduce the crawl rate for your site. If your
                site returns a 500 for a prolonged period, Googlebot may stop
                crawling some of your URLs." — Google Search Central
                documentation.
              </p>
            </div>
          </section>

          {/* Core Web Vitals */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">
              Downtime and Core Web Vitals
            </h2>
            <p className="mb-4 text-lg leading-[1.7] text-muted-foreground">
              Core Web Vitals (LCP, INP, CLS) are measured from real user
              data via the Chrome User Experience Report (CrUX). When your
              site is slow or intermittently failing, these metrics degrade.
            </p>

            <div className="grid gap-4 sm:grid-cols-3 mb-6">
              <div className="rounded-lg border border-border bg-card p-5">
                <BarChart className="mb-3 h-6 w-6 text-status-down" />
                <h3 className="font-sans mb-2 font-semibold">LCP spikes</h3>
                <p className="text-lg leading-[1.7] text-body">
                  When your server is struggling before a full outage,
                  response times spike. Largest Contentful Paint jumps from
                  1.5s to 8s. Google records these slow loads from real
                  users, and your CWV score drops.
                </p>
              </div>
              <div className="rounded-lg border border-border bg-card p-5">
                <TrendingDown className="mb-3 h-6 w-6 text-status-degraded" />
                <h3 className="font-sans mb-2 font-semibold">Bounce rate surge</h3>
                <p className="text-lg leading-[1.7] text-body">
                  Users who hit an error page bounce immediately. A spike in
                  bounce rate signals to Google that users aren't finding what
                  they need. Combined with crawl errors, this is a strong
                  negative ranking signal.
                </p>
              </div>
              <div className="rounded-lg border border-border bg-card p-5">
                <AlertTriangle className="mb-3 h-6 w-6 text-status-degraded" />
                <h3 className="font-sans mb-2 font-semibold">Soft 404 traps</h3>
                <p className="text-lg leading-[1.7] text-body">
                  Some servers return a 200 status code with an error page
                  body during partial outages. Google calls these "soft 404s"
                  and they're worse than real errors because Google thinks
                  the page content changed to something useless.
                </p>
              </div>
            </div>
          </section>

          {/* How monitoring helps */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">
              How monitoring protects your SEO
            </h2>
            <p className="mb-6 text-lg leading-[1.7] text-muted-foreground">
              You can't prevent all downtime. Servers crash, deployments fail,
              and DNS providers have outages. But you can minimize how long
              your site stays down, and that's what matters for SEO. The
              difference between a 5-minute outage and a 4-hour outage is
              the difference between "Google didn't notice" and "Google
              deindexed your top-ranking page."
            </p>

            <div className="space-y-6">
              <div className="flex gap-4">
                <Clock className="mt-1 h-5 w-5 shrink-0 text-primary" />
                <div>
                  <h3 className="font-sans mb-1 font-semibold">
                    Faster detection = faster fix
                  </h3>
                  <p className="text-lg leading-[1.7] text-body">
                    With 30-second checks, Uptrack detects an outage within
                    30 seconds and alerts you once several locations confirm it. Compare that to
                    discovering downtime when a customer emails you 3 hours
                    later. Every minute you shave off detection time is a
                    minute less that Googlebot might encounter your error.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <Zap className="mt-1 h-5 w-5 shrink-0 text-primary" />
                <div>
                  <h3 className="font-sans mb-1 font-semibold">
                    Instant alerts to the right channel
                  </h3>
                  <p className="text-lg leading-[1.7] text-body">
                    An email you check twice a day isn't enough. Get alerts
                    in Slack, Discord, or via webhook to trigger automated
                    recovery. The goal is to fix the issue before Googlebot's
                    next scheduled crawl. If you can recover within minutes,
                    Google may never record the outage.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <Search className="mt-1 h-5 w-5 shrink-0 text-primary" />
                <div>
                  <h3 className="font-sans mb-1 font-semibold">
                    Response body assertions catch soft 404s
                  </h3>
                  <p className="text-lg leading-[1.7] text-body">
                    Standard monitoring only checks the status code. But if
                    your app returns 200 with "Something went wrong" in the
                    body, that's a soft 404 Google will penalize. Uptrack's
                    response body assertions detect these silent failures that
                    simple ping monitoring misses entirely.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <Globe className="mt-1 h-5 w-5 shrink-0 text-primary" />
                <div>
                  <h3 className="font-sans mb-1 font-semibold">
                    SSL monitoring prevents HTTPS errors
                  </h3>
                  <p className="text-lg leading-[1.7] text-body">
                    An expired SSL certificate causes browsers to show a
                    security warning. Googlebot treats HTTPS errors seriously.
                    If your certificate expires and isn't renewed for a day,
                    Google may temporarily drop your pages from results.
                    SSL monitoring alerts you before expiry, not after.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Real-world scenarios */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">
              Real-world SEO damage scenarios
            </h2>
            <div className="space-y-4">
              <div className="rounded-lg border border-border bg-card p-5">
                <div className="flex items-center gap-2 mb-2">
                  <ShieldCheck className="h-5 w-5 text-primary" />
                  <h3 className="font-sans font-semibold">The deployment that broke everything</h3>
                </div>
                <p className="text-lg leading-[1.7] text-body">
                  You deploy on Friday evening. A bug causes your homepage to
                  return a 500 error. Nobody notices until Monday morning. That's
                  60 hours of downtime. Googlebot crawled your site 4 times
                  during the weekend and recorded 4 consecutive 500 errors.
                  Your homepage drops from position 3 to position 18. It takes
                  3 weeks to recover after the fix.
                </p>
              </div>

              <div className="rounded-lg border border-border bg-card p-5">
                <div className="flex items-center gap-2 mb-2">
                  <ShieldCheck className="h-5 w-5 text-primary" />
                  <h3 className="font-sans font-semibold">The expired SSL certificate</h3>
                </div>
                <p className="text-lg leading-[1.7] text-body">
                  Your Let's Encrypt auto-renewal fails silently. Your site
                  shows a "Not Secure" warning for 12 hours. Chrome users see
                  a scary interstitial. Bounce rate hits 95%. Google detects the
                  HTTPS failure and temporarily removes your site from secure
                  search results. You lose 30% of organic traffic for a week.
                </p>
              </div>

              <div className="rounded-lg border border-border bg-card p-5">
                <div className="flex items-center gap-2 mb-2">
                  <ShieldCheck className="h-5 w-5 text-primary" />
                  <h3 className="font-sans font-semibold">The intermittent database issue</h3>
                </div>
                <p className="text-lg leading-[1.7] text-body">
                  Your database connection pool is exhausted during peak hours.
                  Your app returns 200 but with "Database connection failed"
                  in the response body. Standard monitoring says "all good."
                  Googlebot indexes the error text. Your page shows "Database
                  connection failed" as a search result snippet. Users stop
                  clicking.
                </p>
              </div>
            </div>

            <p className="mt-6 text-lg leading-[1.7] text-muted-foreground">
              Every one of these scenarios is preventable with proper
              monitoring. Detect downtime in 1 minute, free. Fix it before
              Googlebot notices.
            </p>
          </section>

          {/* Prevention checklist */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">
              SEO downtime prevention checklist
            </h2>
            <div className="space-y-3">
              {[
                'Monitor your homepage, key landing pages, and sitemap URL independently',
                'Use response body assertions to catch soft 404s and error messages',
                'Set up SSL expiry monitoring with alerts at least 7 days before expiry',
                'Use 30-second check intervals for pages that drive organic traffic',
                'Configure instant alerts (Slack/Discord) so you can fix issues before the next crawl',
                'After any outage, check Google Search Console for new crawl errors',
                'After fixing an outage, use "Request Indexing" in Search Console to accelerate re-crawl',
                'Monitor response time — pages consistently over 3 seconds hurt Core Web Vitals',
              ].map((item, i) => (
                <div key={i} className="flex gap-3">
                  <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  <p className="text-lg leading-[1.7] text-body">{item}</p>
                </div>
              ))}
            </div>
          </section>

          {/* CTA */}
          <div className="rounded-lg border border-border bg-muted p-8 text-center">
            <Search className="mx-auto mb-4 h-8 w-8 text-primary" />
            <h2 className="font-sans mb-3 text-xl font-semibold">
              Protect Your SEO Rankings
            </h2>
            <p className="mb-6 text-lg leading-[1.7] text-muted-foreground">
              All monitors at 30-second checks, from $3/mo.
              30-day money-back guarantee.
            </p>
            <Button size="lg" asChild>
              <Link to="/pricing">
                See Plans
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </article>
      </>

      </>
  )
}
